/**
 * SidebarGlobalSearch — compact quick-nav input at the top of AppSidebar.
 * Filters a static list of pages and navigates on click.
 */
import { useMemo, useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  X,
  BarChart3,
  Dumbbell,
  CalendarDays,
  Users,
  BookOpen,
  Award,
  LayoutGrid,
  User,
  Settings,
} from "lucide-react";
import { cn } from "@/lib/utils";

type QuickNavItem = {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
};

const QUICK_NAV: QuickNavItem[] = [
  { title: "Accueil", url: "/dashboard", icon: BarChart3 },
  { title: "Programmes", url: "/programs", icon: Dumbbell },
  { title: "Planning", url: "/planning", icon: CalendarDays },
  { title: "Communauté", url: "/community", icon: Users },
  { title: "Bibliothèque", url: "/library", icon: BookOpen },
  { title: "Mérites", url: "/merits", icon: Award },
  { title: "Modules", url: "/modules", icon: LayoutGrid },
  { title: "Profil", url: "/athlete-profile", icon: User },
  { title: "Réglages", url: "/settings", icon: Settings },
];

export function SidebarGlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return QUICK_NAV;
    return QUICK_NAV.filter((item) => item.title.toLowerCase().includes(q));
  }, [query]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [open]);

  const handleSelect = (url: string) => {
    navigate(url);
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative mx-2 mt-2 mb-3">
      <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/60" />
      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Rechercher..."
        className={cn(
          "w-full h-8 pl-7 pr-7 rounded-lg bg-muted/20 border border-border/30",
          "text-sm text-foreground placeholder:text-muted-foreground/50",
          "focus:outline-none focus:bg-muted/30 transition-colors",
        )}
        aria-label="Recherche rapide"
      />
      {query && (
        <button
          type="button"
          onClick={() => {
            setQuery("");
            setOpen(false);
          }}
          className="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-muted-foreground/60 hover:text-foreground transition-colors"
          aria-label="Effacer la recherche"
        >
          <X className="h-3 w-3" />
        </button>
      )}

      {open && filtered.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 rounded-lg border border-border/40 bg-popover shadow-md overflow-hidden">
          <ul className="max-h-64 overflow-y-auto py-1">
            {filtered.map((item) => (
              <li key={item.url}>
                <button
                  type="button"
                  onClick={() => handleSelect(item.url)}
                  className="w-full flex items-center gap-2 px-2.5 py-1.5 text-sm text-foreground hover:bg-sidebar-accent transition-colors text-left"
                >
                  <item.icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="truncate">{item.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {open && query && filtered.length === 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 rounded-lg border border-border/40 bg-popover shadow-md px-3 py-2">
          <p className="text-xs text-muted-foreground">Aucun résultat</p>
        </div>
      )}
    </div>
  );
}
